import React from 'react';
import { Helmet } from 'react-helmet-async';

const TermsAndConditions = () => {
  return (
    <>
      <Helmet>
        <title>Terms and Conditions | The APN - Accelerated Professionals Network</title>
        <meta name="description" content="Terms and Conditions for The APN (Accelerated Professionals Network) membership and event participation." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow-lg rounded-lg p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">
              APN Membership Agreement and Policies
            </h1>
            
            <div className="prose max-w-none">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">RECITALS AND DEFINITIONS</h2>
              <p className="mb-4">
                WHEREAS, the Accelerated Professionals Network (APN) is a professional membership organization providing networking, educational, and professional development opportunities; WHEREAS, APN requires all members and participants to comply with its governing documents; NOW, THEREFORE, this Membership Policy Packet is adopted and binding upon all members and participants.
              </p>
              <p className="mb-6">
                <strong>APN</strong> means the Accelerated Professionals Network. <strong>Member</strong> means an individual accepted into membership. <strong>Events</strong> means programs, conferences, activities, or functions organized, sponsored, or endorsed by APN. <strong>Officers and Agents</strong> means directors, officers, employees, volunteers, representatives, successors, and assigns of APN.
              </p>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4">ARTICLE I - TERMS AND CONDITIONS</h2>
              
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Membership and Eligibility</h3>
              <p className="mb-4">
                Membership is open to individuals eighteen years of age or older who submit accurate applications. APN reserves discretion to approve, deny, suspend, or terminate membership.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Dues and Payments</h3>
              <p className="mb-4">
                Dues are set annually by APN. Payment of dues confers membership rights for the term. Dues are non-refundable.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Conduct</h3>
              <p className="mb-4">
                Members shall conduct themselves professionally and respectfully. Harassment, discrimination, abuse, or disruptive conduct is prohibited.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Intellectual Property</h3>
              <p className="mb-4">
                All APN logos, trademarks, publications, and content are property of APN. Unauthorized use is prohibited.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Termination</h3>
              <p className="mb-4">
                APN may terminate membership for violations of these Terms. Appeals must be submitted in writing.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Indemnification</h3>
              <p className="mb-4">
                Members shall indemnify, defend, and hold harmless APN and its Officers and Agents from all claims, liabilities, or expenses arising from membership or violations.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Limitation of Liability</h3>
              <p className="mb-4">
                APN and its Officers and Agents are not liable for indirect, incidental, or consequential damages. Liability is capped at dues paid in the twelve months preceding the claim.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Waiver of Claims</h3>
              <p className="mb-4">
                Members assume all risks associated with participation in APN activities, including illness, injury, communicable diseases, property loss, alcohol consumption, and negligence of third parties, and hereby waive and release all claims against APN and its Officers and Agents.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Dispute Resolution</h3>
              <p className="mb-4">
                Governed by New York law. Disputes shall be mediated in New York and, if unresolved, resolved by binding arbitration before the American Arbitration Association. Class actions are waived.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Amendments</h3>
              <p className="mb-4">
                APN may amend these Terms at any time by posting updated versions online.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Hierarchy</h3>
              <p className="mb-6">
                In the event of conflict, the Membership Agreement controls.
              </p>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4">ARTICLE III - CODE OF CONDUCT AND ANTI-DISCRIMINATION POLICY</h2>
              
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Anti-Discrimination Policy Statement</h3>
              <p className="mb-4">
                APN is committed to maintaining an environment free from discrimination, harassment, and retaliation.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Prohibited Conduct</h3>
              <p className="mb-4">
                Discrimination, harassment, or retaliation based on race, color, religion, gender, age, disability, national origin, sexual orientation, or any protected category under applicable law is strictly prohibited. Additionally, lewd, lascivious, intoxicated, or otherwise disruptive behavior (including conduct that is sexually explicit, aggressive, or inappropriate in public or professional settings) is prohibited and may result in immediate removal from events, suspension, or termination of membership.
              </p>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4">ARTICLE IV - EVENT REGISTRATION TERMS AND CONDITIONS</h2>
              
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Registration and Payment</h3>
              <p className="mb-4">
                Registration for APN events must be completed in advance through designated channels. Payment is required at the time of registration. APN reserves the right to deny registration for failure to pay or for prior violations of APN policies.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Cancellations and Refunds</h3>
              <p className="mb-4">
                Cancellations must be submitted in writing at least forty-eight hours prior to the event. Refunds or credits are provided at APN's sole discretion.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Conduct at Events</h3>
              <p className="mb-4">
                Attendees are expected to conduct themselves professionally and respectfully. Harassment, discrimination, abuse, or disruptive conduct will not be tolerated and may result in removal without refund.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Media and Photography</h3>
              <p className="mb-4">
                APN may photograph, film, or otherwise record events. By attending, participants consent to the use of their image and likeness by APN in promotional materials without compensation.
              </p>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4">ARTICLE V - EVENT ATTENDEE WAIVER AND RELEASE</h2>
              
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Assumption of Risk</h3>
              <p className="mb-4">
                Attendees acknowledge that participation in APN events carries inherent risks, including but not limited to illness, injury, property loss, alcohol consumption, travel-related risks, and exposure to communicable diseases.
              </p>

              <h3 className="text-lg font-semibold text-gray-700 mb-2">Waiver and Release of Claims</h3>
              <p className="mb-4">
                Attendees waive, release, and discharge APN and its Officers and Agents from all claims, liabilities, or causes of action arising from participation in APN events, whether caused by negligence or otherwise.
              </p>

              <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Version:</strong> August 29, 2025 – Master Copy<br />
                  <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsAndConditions;
